import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaStar } from 'react-icons/fa';
import Seo from '../../components/Seo/Seo';
import TestimonialCard from '../../components/TestimonialCard/TestimonialCard';
import { getApprovedFeedback } from '../../utils/api';

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterRating, setFilterRating] = useState(0);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const data = await getApprovedFeedback();
        setTestimonials(data.feedback);
      } catch (err) {
        console.error('Error fetching testimonials:', err);
        setError('Failed to load testimonials. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  const filteredTestimonials =
    filterRating === 0
      ? testimonials
      : testimonials.filter((t) => t.rating === filterRating);

  const averageRating =
    testimonials.length > 0
      ? (testimonials.reduce((sum, t) => sum + t.rating, 0) / testimonials.length).toFixed(1)
      : '0.0';

  const ratingDistribution = [5, 4, 3, 2, 1].map((star) => ({
    star,
    count: testimonials.filter((t) => t.rating === star).length,
    percentage:
      testimonials.length > 0
        ? Math.round(
            (testimonials.filter((t) => t.rating === star).length / testimonials.length) * 100
          )
        : 0,
  }));

  return (
    <div>
      <Seo
        title="Testimonials"
        description="Read verified customer testimonials and reviews for The Skilled Group's pest control and cleaning services."
        canonicalPath="/testimonials"
      />
      {/* Page Header */}
      <div className="page-header">
        <div className="container-custom">
          <h1>Testimonials</h1>
          <p>
            See what our customers have to say about their The Skilled Group experience.
          </p>
        </div>
      </div>

      <section className="section-padding bg-white">
        <div className="container-custom">
          {loading ? (
            <div className="flex justify-center py-20">
              <svg className="animate-spin h-12 w-12 text-primary-500" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-red-500 text-lg">{error}</p>
            </div>
          ) : testimonials.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <FaStar className="text-3xl text-gray-300" />
              </div>
              <h2 className="text-2xl font-heading font-bold text-gray-800 mb-3">
                No Testimonials Yet
              </h2>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                Be the first to share your experience with The Skilled Group. Your feedback helps
                us improve and helps others make informed decisions.
              </p>
              <Link
                to="/feedback"
                className="btn-primary inline-flex items-center gap-2"
              >
                Leave Feedback
                <FaArrowRight className="text-sm" />
              </Link>
            </div>
          ) : (
            <>
              {/* Stats Summary */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
                {/* Average Rating */}
                <div className="bg-primary-50 rounded-2xl p-8 text-center border border-primary-100">
                  <p className="text-5xl font-heading font-bold text-primary-500 mb-2">
                    {averageRating}
                  </p>
                  <div className="flex items-center justify-center gap-1 mb-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <FaStar
                        key={star}
                        className={`text-lg ${
                          star <= Math.round(parseFloat(averageRating))
                            ? 'text-accent-500'
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-gray-500 text-sm">
                    Based on {testimonials.length} review{testimonials.length !== 1 ? 's' : ''}
                  </p>
                </div>

                {/* Rating Distribution */}
                <div className="lg:col-span-2 bg-white rounded-2xl p-8 border border-gray-200">
                  <h3 className="text-lg font-heading font-semibold text-gray-800 mb-4">
                    Rating Distribution
                  </h3>
                  <div className="space-y-3">
                    {ratingDistribution.map((item) => (
                      <button
                        key={item.star}
                        onClick={() =>
                          setFilterRating(filterRating === item.star ? 0 : item.star)
                        }
                        className={`flex items-center gap-3 w-full text-left p-1 rounded transition-colors ${
                          filterRating === item.star ? 'bg-primary-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <span className="text-sm font-medium text-gray-600 w-12">
                          {item.star} star{item.star !== 1 ? 's' : ''}
                        </span>
                        <div className="flex-grow h-3 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-accent-500 rounded-full transition-all duration-500"
                            style={{ width: `${item.percentage}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-500 w-16 text-right">
                          {item.count} ({item.percentage}%)
                        </span>
                      </button>
                    ))}
                  </div>

                  {filterRating > 0 && (
                    <button
                      onClick={() => setFilterRating(0)}
                      className="mt-4 text-sm text-primary-500 hover:text-primary-600 font-medium"
                    >
                      Clear filter — Show all reviews
                    </button>
                  )}
                </div>
              </div>

              {/* Testimonials Grid */}
              {filteredTestimonials.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                  {filteredTestimonials.map((testimonial) => (
                    <TestimonialCard key={testimonial._id} testimonial={testimonial} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-400 text-lg">
                    No reviews with {filterRating} star{filterRating !== 1 ? 's' : ''} rating.
                  </p>
                </div>
              )}

              {/* Leave Feedback CTA */}
              <div className="text-center mt-12 pt-10 border-t border-gray-100">
                <h3 className="text-xl font-heading font-semibold text-gray-800 mb-3">
                  Had a Great Experience?
                </h3>
                <p className="text-gray-500 mb-6 max-w-md mx-auto">
                  We'd love to hear from you! Share your feedback and help others
                    discover The Skilled Group.
                </p>
                <Link
                  to="/feedback"
                  className="btn-primary inline-flex items-center gap-2"
                >
                  Leave Your Feedback
                  <FaArrowRight className="text-sm" />
                </Link>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Testimonials;